import { useState, useEffect } from 'react';
import { X, Cookie, Settings, ExternalLink } from 'lucide-react';

type Language = 'fr' | 'en' | 'es';

const translations = {
  fr: {
    title: 'Nous utilisons des cookies',
    description: 'Nous utilisons des cookies pour améliorer votre expérience, analyser le trafic et personnaliser le contenu. Vous pouvez choisir quels cookies accepter. En savoir plus dans notre',
    policyLink: 'politique de cookies',
    acceptAll: 'Tout accepter',
    necessaryOnly: 'Nécessaires uniquement',
    customize: 'Personnaliser',
    settingsTitle: 'Paramètres des cookies',
    savePreferences: 'Enregistrer mes préférences',
    cancel: 'Annuler',
    categories: {
      necessary: {
        title: 'Cookies strictement nécessaires',
        description: 'Ces cookies sont essentiels au fonctionnement du site. Ils permettent l\'authentification et la navigation de base.',
        alwaysActive: 'Toujours actifs'
      },
      functional: {
        title: 'Cookies fonctionnels',
        description: 'Ces cookies permettent de mémoriser vos préférences (langue, thème) pour améliorer votre expérience.'
      },
      analytics: {
        title: 'Cookies analytiques',
        description: 'Ces cookies nous aident à comprendre comment vous utilisez le site pour l\'améliorer.'
      },
      marketing: {
        title: 'Cookies marketing',
        description: 'Ces cookies sont utilisés pour vous proposer des publicités pertinentes et mesurer l\'efficacité des campagnes.'
      }
    }
  },
  en: {
    title: 'We use cookies',
    description: 'We use cookies to improve your experience, analyze traffic and personalize content. You can choose which cookies to accept. Learn more in our',
    policyLink: 'cookie policy',
    acceptAll: 'Accept all',
    necessaryOnly: 'Necessary only',
    customize: 'Customize',
    settingsTitle: 'Cookie settings',
    savePreferences: 'Save my preferences',
    cancel: 'Cancel',
    categories: {
      necessary: {
        title: 'Strictly necessary cookies',
        description: 'These cookies are essential for the site to function. They enable authentication and basic navigation.',
        alwaysActive: 'Always active'
      },
      functional: {
        title: 'Functional cookies',
        description: 'These cookies allow us to remember your preferences (language, theme) to improve your experience.'
      },
      analytics: {
        title: 'Analytics cookies',
        description: 'These cookies help us understand how you use the site to improve it.'
      },
      marketing: {
        title: 'Marketing cookies',
        description: 'These cookies are used to show you relevant ads and measure campaign effectiveness.'
      }
    }
  },
  es: {
    title: 'Usamos cookies',
    description: 'Usamos cookies para mejorar su experiencia, analizar el tráfico y personalizar el contenido. Puede elegir qué cookies aceptar. Más información en nuestra',
    policyLink: 'política de cookies',
    acceptAll: 'Aceptar todo',
    necessaryOnly: 'Solo necesarias',
    customize: 'Personalizar',
    settingsTitle: 'Configuración de cookies',
    savePreferences: 'Guardar mis preferencias',
    cancel: 'Cancelar',
    categories: {
      necessary: {
        title: 'Cookies estrictamente necesarias',
        description: 'Estas cookies son esenciales para el funcionamiento del sitio. Permiten la autenticación y la navegación básica.',
        alwaysActive: 'Siempre activas'
      },
      functional: {
        title: 'Cookies funcionales',
        description: 'Estas cookies nos permiten recordar sus preferencias (idioma, tema) para mejorar su experiencia.'
      },
      analytics: {
        title: 'Cookies analíticas',
        description: 'Estas cookies nos ayudan a comprender cómo utiliza el sitio para mejorarlo.'
      },
      marketing: {
        title: 'Cookies de marketing',
        description: 'Estas cookies se utilizan para mostrarle anuncios relevantes y medir la efectividad de las campañas.'
      }
    }
  }
};

interface CookiePreferences {
  necessary: boolean;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
}

interface CookieConsent {
  preferences: CookiePreferences;
  timestamp: string;
  version: string;
}

interface CookieConsentBannerProps {
  language?: Language;
}

export const CookieConsentBanner = ({ language = 'fr' }: CookieConsentBannerProps) => {
  const t = translations[language];
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    functional: false,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const consentStr = localStorage.getItem('cookie_consent');
    if (!consentStr) {
      setShowBanner(true);
    } else {
      try {
        const consent: CookieConsent = JSON.parse(consentStr);
        applyCookiePreferences(consent.preferences);
      } catch (e) {
        setShowBanner(true);
      }
    }
  }, []);

  const acceptAll = () => {
    const allAccepted: CookiePreferences = {
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true,
    };
    savePreferences(allAccepted);
  };

  const acceptNecessary = () => {
    const necessaryOnly: CookiePreferences = {
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false,
    };
    savePreferences(necessaryOnly);
  };

  const savePreferences = (prefs: CookiePreferences) => {
    const consent: CookieConsent = {
      preferences: prefs,
      timestamp: new Date().toISOString(),
      version: '1.0',
    };
    
    localStorage.setItem('cookie_consent', JSON.stringify(consent));
    
    // Envoyer au backend (optionnel, pour analytics)
    fetch('/api/cookies/consent', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(prefs),
    }).catch(() => {
      // Silently fail, not critical
    });

    setShowBanner(false);
    setShowSettings(false);
    
    applyCookiePreferences(prefs);
  };

  const applyCookiePreferences = (prefs: CookiePreferences) => {
    // Nettoyer les cookies/localStorage non autorisés
    if (!prefs.analytics) {
      // Supprimer les cookies analytics (Google Analytics, etc.)
      const cookies = document.cookie.split(';');
      for (const cookie of cookies) {
        const name = cookie.split('=')[0].trim();
        if (name.startsWith('_ga') || name.startsWith('_gid')) {
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
        }
      }
    }
    
    if (!prefs.functional) {
      // Supprimer les préférences non essentielles
      const functionalKeys = ['theme', 'sidebar_collapsed', 'lang', 'onboarding_completed', 'chat_widget_minimized'];
      functionalKeys.forEach(key => {
        localStorage.removeItem(key);
      });
    }
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Banner principale */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-zinc-900 border-t border-zinc-800 shadow-2xl animate-slide-up">
        <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex gap-4">
            <Cookie className="w-6 h-6 text-emerald-500 flex-shrink-0 mt-1" />
            
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-white mb-2">
                {t.title}
              </h3>
              <p className="text-sm text-zinc-300 mb-4">
                {t.description}{' '}
                <a href="/cookies-policy" className="text-emerald-400 hover:text-emerald-300 underline">
                  {t.policyLink}
                </a>.
              </p>
              
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={acceptAll}
                  className="px-6 py-2.5 bg-emerald-500 text-white rounded-lg font-medium hover:bg-emerald-400 transition-colors shadow-lg hover:shadow-emerald-500/20"
                >
                  {t.acceptAll}
                </button>
                
                <button
                  onClick={acceptNecessary}
                  className="px-6 py-2.5 bg-zinc-700 text-white rounded-lg font-medium hover:bg-zinc-600 transition-colors"
                >
                  {t.necessaryOnly}
                </button>
                
                <button
                  onClick={() => setShowSettings(true)}
                  className="px-6 py-2.5 border border-zinc-600 text-zinc-300 rounded-lg font-medium hover:bg-zinc-800 transition-colors flex items-center gap-2"
                >
                  <Settings className="w-4 h-4" />
                  {t.customize}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de paramètres */}
      {showSettings && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in">
          <div className="bg-zinc-900 rounded-2xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto border border-zinc-800">
            <div className="p-6 border-b border-zinc-800 flex items-center justify-between sticky top-0 bg-zinc-900 z-10">
              <h2 className="text-xl font-bold text-white">{t.settingsTitle}</h2>
              <button
                onClick={() => setShowSettings(false)}
                className="text-zinc-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Nécessaires */}
              <div className="flex items-start justify-between p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold text-white">{t.categories.necessary.title}</h3>
                    <span className="text-xs px-2 py-0.5 bg-emerald-500/20 text-emerald-400 rounded-full">
                      {t.categories.necessary.alwaysActive}
                    </span>
                  </div>
                  <p className="text-sm text-zinc-400">
                    {t.categories.necessary.description} Ils ne peuvent pas être désactivés.
                  </p>
                  <p className="text-xs text-zinc-500 mt-2">
                    Exemples : token d'authentification, préférences de consentement
                  </p>
                </div>
                <div className="ml-4">
                  <input
                    type="checkbox"
                    checked={true}
                    disabled
                    className="w-5 h-5 rounded border-zinc-600 bg-zinc-700 text-emerald-500 cursor-not-allowed opacity-50"
                  />
                </div>
              </div>

              {/* Fonctionnels */}
              <div className="flex items-start justify-between p-4 bg-zinc-800/50 rounded-lg border border-zinc-700 hover:border-zinc-600 transition-colors">
                <div className="flex-1">
                  <h3 className="font-semibold text-white mb-2">{t.categories.functional.title}</h3>
                  <p className="text-sm text-zinc-400">
                    {t.categories.functional.description}
                  </p>
                  <p className="text-xs text-zinc-500 mt-2">
                    Exemples : langue préférée, thème clair/sombre, état de la sidebar
                  </p>
                </div>
                <div className="ml-4">
                  <input
                    type="checkbox"
                    checked={preferences.functional}
                    onChange={(e) => setPreferences({...preferences, functional: e.target.checked})}
                    className="w-5 h-5 rounded border-zinc-600 bg-zinc-800 text-emerald-500 focus:ring-emerald-500 focus:ring-offset-0 cursor-pointer"
                  />
                </div>
              </div>

              {/* Analytiques */}
              <div className="flex items-start justify-between p-4 bg-zinc-800/50 rounded-lg border border-zinc-700 hover:border-zinc-600 transition-colors">
                <div className="flex-1">
                  <h3 className="font-semibold text-white mb-2">{t.categories.analytics.title}</h3>
                  <p className="text-sm text-zinc-400">
                    {t.categories.analytics.description} Aucune donnée personnelle n'est collectée.
                  </p>
                  <p className="text-xs text-zinc-500 mt-2">
                    Exemples : Google Analytics (si activé)
                  </p>
                </div>
                <div className="ml-4">
                  <input
                    type="checkbox"
                    checked={preferences.analytics}
                    onChange={(e) => setPreferences({...preferences, analytics: e.target.checked})}
                    className="w-5 h-5 rounded border-zinc-600 bg-zinc-800 text-emerald-500 focus:ring-emerald-500 focus:ring-offset-0 cursor-pointer"
                  />
                </div>
              </div>

              {/* Marketing */}
              <div className="flex items-start justify-between p-4 bg-zinc-800/50 rounded-lg border border-zinc-700 hover:border-zinc-600 transition-colors">
                <div className="flex-1">
                  <h3 className="font-semibold text-white mb-2">{t.categories.marketing.title}</h3>
                  <p className="text-sm text-zinc-400">
                    {t.categories.marketing.description} Actuellement, nous n'utilisons pas de cookies marketing.
                  </p>
                  <p className="text-xs text-zinc-500 mt-2">
                    Exemples : Publicités ciblées (non utilisé actuellement)
                  </p>
                </div>
                <div className="ml-4">
                  <input
                    type="checkbox"
                    checked={preferences.marketing}
                    onChange={(e) => setPreferences({...preferences, marketing: e.target.checked})}
                    className="w-5 h-5 rounded border-zinc-600 bg-zinc-800 text-emerald-500 focus:ring-emerald-500 focus:ring-offset-0 cursor-pointer"
                  />
                </div>
              </div>

              {/* Info supplémentaire */}
              <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                <div className="flex items-start gap-3">
                  <ExternalLink className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-blue-300 mb-2">
                      Pour plus d'informations sur notre utilisation des cookies et vos droits, consultez notre politique de cookies.
                    </p>
                    <a
                      href="/cookies-policy"
                      className="text-sm text-blue-400 hover:text-blue-300 underline inline-flex items-center gap-1"
                    >
                      Lire la politique complète
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-zinc-800 flex gap-3 sticky bottom-0 bg-zinc-900">
              <button
                onClick={() => savePreferences(preferences)}
                className="flex-1 px-6 py-3 bg-emerald-500 text-white rounded-lg font-medium hover:bg-emerald-400 transition-colors shadow-lg hover:shadow-emerald-500/20"
              >
                {t.savePreferences}
              </button>
              <button
                onClick={() => setShowSettings(false)}
                className="px-6 py-3 border border-zinc-700 text-zinc-300 rounded-lg font-medium hover:bg-zinc-800 transition-colors"
              >
                {t.cancel}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        
        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
        
        .animate-fade-in {
          animation: fade-in 0.2s ease-out;
        }
      `}</style>
    </>
  );
};
